import { ACTToolbarService } from 'src/app/angularJS/services/toolbar.service';
import { QueryBuilder } from 'src/app/angularJS/services/query-builder.service';
import { PaymentService } from 'src/app/angularJS/services/payments/payments.service';
import { CustomerService } from 'src/app/angularJS/services/customer/customer.service';
import { VariantService } from 'src/app/angularJS/services/variant/variant.service';
import { BreadcrumbService } from 'src/app/angularJS/services/breadcrumbs/breadcrumb.service';
import { UsageEventProductMappingsService } from 'src/app/angularJS/services/usageEventProductMappings/usageEventProductMappings.service';
import { JourneyService } from 'src/app/angularJS/services/journey/journey.service';

/**
 * A class that contains properties and methods relating to viewing the details of a single payment
 */
export class ChargeAccountPaymentDetailsController {
  public static $inject = [
    'paymentRequest',
    'ACTToolbarService',
    'chargeAccount',
    'QueryBuilder',
    'paymentService',
    'customerService',
    'variantService',
    'breadcrumbService',
    'usageEventProductMappingsService',
    'journeyService',
    'transportOperatorService'
  ];
  public expandedItem: InterACT.Interfaces.UsageEventProductMappings.UsageEventProductMapping;
  public usageEventProductMappings: Array<InterACT.Interfaces.UsageEventProductMappings.UsageEventProductMapping> = [];
  public usageEventProductMappingToProductDictionary: InterACT.Interfaces.IDictionary<InterACT.Interfaces.Catalogues.Variant> = {};
  public usageEventProductMappingToJourneysDictionary: InterACT.Interfaces.IDictionary<Array<InterACT.Interfaces.Journeys.Journey>> = {};
  public usageEventProductMappingToPurchaseDateDictionary: InterACT.Interfaces.IDictionary<string> = {};
  public customer: InterACT.Interfaces.Customers.Customer;

  constructor(
    public paymentRequest: InterACT.Interfaces.Payments.PaymentRequest,
    private ActToolbarService: ACTToolbarService,
    private chargeAccount: InterACT.Interfaces.ChargeAccounts.ChargeAccount,
    private queryBuilder: QueryBuilder,
    private paymentService: PaymentService,
    private customerService: CustomerService,
    private variantService: VariantService,
    private breadcrumbService: BreadcrumbService,
    private usageEventProductMappingsService: UsageEventProductMappingsService,
    private journeyService: JourneyService
  ) {
    this.activate();
  }

  /**
* A method that gets the mode of a given journey
* @param journey the journey to get the mode for
*/
  public getJourneyMode(journey: InterACT.Interfaces.Journeys.Journey) {
    if (!journey.journeyLegs || journey.journeyLegs.length === 0) {
      return 'NA';
    }

    const firstJourneyLegMode = journey.journeyLegs[0].mode;
    const otherModes = journey.journeyLegs.filter((journeyLeg) => {
      return journeyLeg.mode !== firstJourneyLegMode;
    });

    return otherModes.length > 0 ? 'MULTIPLE' : firstJourneyLegMode;
  }

  /**
   * A method that determines a payment type from the payment request mode
   *
   * @returns A translatable string determined from the payment request mode
   */
  public getPaymentType = () => {
    switch (this.paymentRequest.mode) {
      case 'AUTH_AND_SETTLE':
        return 'HEADING.CHARGE';
      case 'REFUND':
        return 'HEADING.REFUND';
    }
  }

  /**
   * A method that toggles the expansion state for the supplied usage event product mapping
   * @param usageEventProductMapping the usage event product mapping to toggle the expansion for
   */
  public toggleExpansion = (usageEventProductMapping: InterACT.Interfaces.UsageEventProductMappings.UsageEventProductMapping) => {
    if (this.expandedItem === usageEventProductMapping) {
      this.expandedItem = undefined;
    } else {
      this.expandedItem = usageEventProductMapping;
    }
  }

  /**
   * A method that retrieves all of the data necessary for view
   */
  private getData() {
    const usageEventProductMappingIds = this.paymentRequest.items.map((usageEventProductMappingLink) => {
      return usageEventProductMappingLink.split('/').pop();
    });

    if (usageEventProductMappingIds.length === 0) {
      return;
    }

    const query = this.queryBuilder.newParam().fieldIn('id', usageEventProductMappingIds).getParam();

    this.usageEventProductMappingsService.getList(1, 1000, query).then((response) => {
      const usageEventProductMappings = response.usageEventProductMappings.map((mappingResponse) => {
        this.usageEventProductMappingToPurchaseDateDictionary[mappingResponse.usageEventProductMapping.id] = <any>mappingResponse.meta.created;
        return mappingResponse.usageEventProductMapping;
      });

      for (let c = 0; c < usageEventProductMappings.length; c++) {
        const usageEventProductMapping = usageEventProductMappings[c];

        this.usageEventProductMappings.push(usageEventProductMapping);
        const journeyids = usageEventProductMapping.relatedItems.map((relatedItem) => {
          return relatedItem.link.split('/').pop();
        });

        this.getProductForUsageEventProductMapping(usageEventProductMapping.product.link, usageEventProductMapping.id);

        const query = this.queryBuilder.newParam().fieldIn('id', journeyids).getParam();
        this.journeyService.getList(1, 3000, query).then((response: InterACT.Interfaces.Journeys.JourneysResponse) => {
          this.usageEventProductMappingToJourneysDictionary[usageEventProductMapping.id] = response.journeys;
        });
      }
    });
  }

  /**
 * A method that retrieves a product for a given usage event product mapping.
 * @param productVariantLink the link to the product variant to retrieve
 * @param usageEventProductMappingId the usage event product mapping that the product variant is associated with
 */
  private getProductForUsageEventProductMapping(productVariantLink: string, usageEventProductMappingId: string) {
    const minimumNumberOfLinkParts = 7;
    const indexOfProductId = 4;
    const indexOfCatalogId = 2;
    const indexOfVariantIdWithQString = 6;
    const linkParts = productVariantLink.split('/');

    if (linkParts.length < minimumNumberOfLinkParts) {
      return;
    }

    const catalogueId = linkParts[indexOfCatalogId];
    const productId = linkParts[indexOfProductId];
    const variantIdWithQueryString = linkParts[indexOfVariantIdWithQString];
    const variantId = variantIdWithQueryString.indexOf('?') !== -1 ? variantIdWithQueryString.split('?')[0] : variantIdWithQueryString;

    this.variantService.getVariant(catalogueId, parseInt(productId), parseInt(variantId)).then((product) => {
      this.usageEventProductMappingToProductDictionary[usageEventProductMappingId] = product;
    });
  }

  /**
   * A method that retrieves a payment token for a given token id
   * @param id the id of the payment token to retrieve
   */
  private getPaymentToken(id: string) {
    return this.paymentService.getPaymentToken(id);
  }

  /**
   * A method that is called during controller instantiating to setup controller data
   */
  private activate() {
    this.getData();


    const customerId = this.chargeAccount.customer ? this.chargeAccount.customer.link.split('/').pop() : undefined;

    if (customerId) {
      this.customerService.getCustomer(customerId).then((customer) => {
        this.customer = customer;

        const paymentTokenLinkParts = this.chargeAccount.paymentToken[0].link.split('/');

        if (paymentTokenLinkParts.length < 3) {
          return;
        }

        const paymentTokenId = paymentTokenLinkParts[2];
        this.getPaymentToken(paymentTokenId).then((response) => {
          this.breadcrumbService.setBreadcrumbs([{
            name: 'CHARGE_ACCOUNT',
            sref: 'charge-account-search'
          }, {
            name: this.upperCaseFirstLetter(this.customer.title) + ' ' + this.customer.forename + ' ' + this.customer.surname,
            sref: 'charge-account-summary',
            params: {
              chargeAccountId: this.chargeAccount.id
            }
          }, {
            name: 'PAYMENTS',
            sref: 'charge-account-payments',
            params: {
              chargeAccountId: this.chargeAccount.id
            }
          }, {
            name: this.paymentRequest.paymentReference,
            sref: 'charge-account-payment-details',
            params: {
              paymentRequestId: this.paymentRequest.id
            }
          }]);
        });
        this.ActToolbarService.getToolbar('primary').then((toolbar) => {
          toolbar.setTitle(this.upperCaseFirstLetter(this.customer.title) + ' ' + this.customer.forename + ' ' + this.customer.surname);
        });

        this.ActToolbarService.getToolbar('primary').then((toolbar) => {
          toolbar.setTitle(this.upperCaseFirstLetter(this.customer.title) + ' ' + this.customer.forename + ' ' + this.customer.surname);

          toolbar.setTabs([
            {
              label: 'SUMMARY',
              sref: 'charge-account-summary({ chargeAccountId: \'' + this.chargeAccount.id + '\' })'
            },
            {
              label: 'USAGE',
              sref: 'charge-account-journeys({ chargeAccountId: \'' + this.chargeAccount.id + '\' })'
            },
            {
              label: 'PAYMENTS',
              sref: 'charge-account-payments({ chargeAccountId: \'' + this.chargeAccount.id + '\' })'
            },
            {
              label: 'PRODUCTS',
              sref: 'charge-account-products({ chargeAccountId: \'' + this.chargeAccount.id + '\' })'
            }
          ]);
        });
      });
    } else {
      this.ActToolbarService.getToolbar('primary').then((toolbar) => {
        toolbar.setTitle('ANONYMOUS_CHARGE_ACCOUNT');


        toolbar.setTabs([
          {
            label: 'SUMMARY',
            sref: 'charge-account-summary({ chargeAccountId: \'' + this.chargeAccount.id + '\' })'
          },
          {
            label: 'USAGE',
            sref: 'charge-account-journeys({ chargeAccountId: \'' + this.chargeAccount.id + '\' })'
          },
          {
            label: 'PAYMENTS',
            sref: 'charge-account-payments({ chargeAccountId: \'' + this.chargeAccount.id + '\' })'
          },
          {
            label: 'PRODUCTS',
            sref: 'charge-account-products({ chargeAccountId: \'' + this.chargeAccount.id + '\' })'
          }
        ]);
      });

      this.breadcrumbService.setBreadcrumbs([{
        name: 'CHARGE_ACCOUNT',
        sref: 'charge-account-search'
      }, {
        name: 'ANONYMOUS_CHARGE_ACCOUNT',
        sref: 'charge-account-summary',
        params: {
          chargeAccountId: this.chargeAccount.id
        }
      }, {
        name: 'PAYMENTS',
        sref: 'charge-account-payments',
        params: {
          chargeAccountId: this.chargeAccount.id
        }
      }, {
        name: this.paymentRequest.paymentReference,
        sref: 'charge-account-payment-details',
        params: {
          paymentRequestId: this.paymentRequest.id
        }
      }]);

    };
  }

  /**
   * A method that returns a given string with the first letter capitalised
   * @param string the string to covert to first letter upper case
   */
  private upperCaseFirstLetter(string: string) {
    string = string.toLowerCase();
    return string.charAt(0).toUpperCase() + string.slice(1);
  }
}
